import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { once } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import setField from 'webapp/helpers/set-field';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomQuerySetupCommonDimensionSelectorComponent from 'webapp/components/custom-query/setup/common/dimension-selector/index';
import CustomQuerySetupCommonPreviewChartComponent from 'webapp/components/custom-query/setup/common/preview-chart';
import type { Args } from 'webapp/components/custom-query/setup/wizard/common/step-common';
import CustomQuerySetupWizardSheetControlsComponent from 'webapp/components/custom-query/setup/wizard/sheet/controls';
import FormErrorComponent from 'webapp/components/form-error';
import FormInputComponent from 'webapp/components/form/input';
import MetricEditEnsureUniquenessComponent from 'webapp/components/metric/edit/ensure-uniqueness';
import MetricEditFormatComponent from 'webapp/components/metric/edit/format';
import MetricEditHistoricalDataPersistenceComponent from 'webapp/components/metric/edit/historical-data-persistence';
import MetricEditInvertComponent from 'webapp/components/metric/edit/invert';
import MetricEditMetricTypePropertiesComponent from 'webapp/components/metric/edit/metric-type-properties';
import QueryBuilderFieldMetricDefinitionBaseComponent from 'webapp/components/query-builder/field/metric-definition-base';

import type Source from 'webapp/models-custom/source';

export default abstract class CustomQuerySetupWizardCommonStepMetricDefinitionComponent<
	T extends Args,
> extends QueryBuilderFieldMetricDefinitionBaseComponent<T> {
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;

	@tracked submitInProgress = false;

	@tracked tmpQueryName = '';

	abstract getInitialQueryName(): string;

	constructor(owner: Owner, args: T) {
		super(owner, args);

		if (this.args.model.isNew) {
			const initialQueryName = this.getInitialQueryName();
			this.args.model.queryName = initialQueryName;
			this.args.form.previewBlock.name = initialQueryName;
		}
		this.tmpQueryName = this.args.model.queryName; // set tmp for edit

		this.setInitialData();

		once(this, this.queryPreview);
	}

	get showReadMore(): boolean {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	get isNextDisabled(): boolean {
		return this.args.form.isSaveButtonDisabled;
	}

	queryPreview(): void {
		void this.args.form.queryPreview();
	}

	@action
	changeQueryName(): void {
		const newName = this.tmpQueryName.trim();
		if (newName && newName !== this.args.model.queryName) {
			this.args.model.queryName = newName;
			this.args.form.previewBlock.name = newName;
			this.tmpQueryName = newName;
		}
	}

	@action
	changeFormat(newValue: string): void {
		this.updateMetricAndSave({
			format: newValue,
		});
	}

	@action
	changeChangeInvert(newValue: boolean): void {
		this.updateMetricAndSave({
			changeInvert: newValue,
		});
	}

	@action
	goNext(): void {
		this.submitInProgress = true;

		this.mmCustomQueriesService
			.saveCustomQuery(this.args.model, this.args.form, this.args.origin)
			.then(
				() => {
					this.args.form.previewTakingTooLongInfolayer?.close();
					this.args.goNext();
				},
				() => {
					// do nothing, error is shown already
				},
			)
			.finally(() => {
				if (isAlive(this)) {
					this.submitInProgress = false;
				}
			});
	}

	// ---- start stuff for QueryBuilderFormMetricDefinitionBaseComponent ----

	get source(): Source {
		return this.args.model.sourceObj!;
	}

	get isDisabled(): boolean {
		return this.submitInProgress;
	}

	get isPeriodDataTypeHidden(): boolean {
		return false;
	}

	get isAllValuesDataTypeHidden(): boolean {
		return false;
	}

	// ---- end stuff for QueryBuilderFormMetricDefinitionBaseComponent ----
	<template>
		<div class="step-ct" {{willDestroy this.cleanupMetric}}>

			<div class="top">
				<h3>Almost there - let’s add some finishing touches</h3>
				{{yield}}
				{{! .toggle-manual button }}
			</div>

			<FormErrorComponent @errors={{@form.errors}} />

			<div class="define-ct">
				<div class="left">

					<CustomQuerySetupCommonPreviewChartComponent @form={{@form}} />

					{{#if @form.availableDimensions}}
						<CustomQuerySetupCommonDimensionSelectorComponent
							@form={{@form}}
							@theme="dark"
						/>
					{{/if}}

					<div class="bottom">
						<div class="format">
							<MetricEditFormatComponent
								@selected={{this.normalizedMetricSettingsData.format}}
								@onChange={{this.changeFormat}}
								@isDisabled={{this.submitInProgress}}
								@skipExampleLabels={{true}}
							/>
						</div>
						<a
							class="advanced-link"
							role="button"
							{{on "click" this.openMetricDefinitionPopup}}
						><CommonUiIconComponent @name="Settings" @size="sm" />Advanced
						</a>
					</div>

				</div>
				<div class="right">
					<div class="row name">
						<strong>Name your metric</strong>
						<FormInputComponent
							name="metrickey_name"
							maxlength="50"
							required={{true}}
							disabled={{this.submitInProgress}}
							@value={{this.tmpQueryName}}
							@focusOut={{this.changeQueryName}}
							@loseFocusOnEnter={{true}}
							@onEmpty={{setField target=this property="tmpQueryName"}}
							@hideFloatLabel={{true}}
						/>
					</div>

					<div class="row data-agg">
						<strong>Data aggregation</strong>

						<MetricEditMetricTypePropertiesComponent
							@model={{hash
								metricType=this.normalizedMetricSettingsData.metricType
								aggregationFunction=this.normalizedMetricSettingsData.aggregationFunction
								granulationOrigin=this.normalizedMetricSettingsData.granulationOrigin
							}}
							@submitInProgress={{this.submitInProgress}}
							@onChange={{this.updateMetricTypeProperties}}
							@hidePeriodDataType={{this.isPeriodDataTypeHidden}}
							@hideAllValuesDataType={{this.isAllValuesDataTypeHidden}}
						/>

						<p>{{{this.infoTooltipText}}}</p>
					</div>

					{{#if this.showEnsureUnique}}
						<MetricEditEnsureUniquenessComponent
							@model={{hash
								ensureUnique=this.normalizedMetricSettingsData.ensureUnique
								isAppend=this.normalizedMetricSettingsData.isAppend
							}}
							@sourceType={{this.source.sourceType}}
							@submitInProgress={{this.submitInProgress}}
							@onChange={{this.changeEnsureUniquenessSettings}}
						/>
					{{/if}}

					{{#if this.showHistoricalDataPersistence}}
						<MetricEditHistoricalDataPersistenceComponent
							@selected={{this.normalizedMetricSettingsData.isHistoricalDataPersistenceEnabled}}
							@submitInProgress={{this.submitInProgress}}
							@onChange={{this.changeHistoricalDataPersistence}}
						/>
					{{/if}}

					<MetricEditInvertComponent
						@value={{this.normalizedMetricSettingsData.changeInvert}}
						@isDisabled={{this.submitInProgress}}
						@showReadMore={{this.showReadMore}}
						@onChange={{this.changeChangeInvert}}
					/>

				</div>
			</div>

		</div>

		{{! controlsNode could still be unrendered }}
		{{#if @controlsNode}}
			{{#in-element @controlsNode}}
				<CustomQuerySetupWizardSheetControlsComponent
					@showBack={{true}}
					@isButtonDisabled={{this.isNextDisabled}}
					@submitInProgress={{this.submitInProgress}}
					@goNext={{this.goNext}}
					@goPrevious={{@goPrevious}}
				/>
			{{/in-element}}
		{{/if}}
	</template>
}
